import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Heart, Plus, Play, Pause, Trash2, Image, Mic, Type, Shuffle, Film } from 'lucide-react';
import HopeReelCreator from './HopeReelCreator';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface HopeKitItem {
  id?: string;
  type: 'text' | 'image' | 'audio' | 'video';
  content_text?: string;
  content_ref?: string;
  caption?: string;
  created_at?: string;
}

interface MemoryBoxProps {
  user: any;
}

export const MemoryBox = ({ user }: MemoryBoxProps) => {
  const [hopeKitItems, setHopeKitItems] = useState<HopeKitItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showHopeReelCreator, setShowHopeReelCreator] = useState(false);
  const [newItem, setNewItem] = useState<HopeKitItem>({
    type: 'text',
    content_text: '',
    caption: ''
  });

  const hopePrompts = [
    "Tell me one thing someone has said that made you feel loved.",
    "Upload a photo of a place where you felt safe.",
    "Record 10 seconds of your favourite memory.",
    "Write 3 things that have helped you survive hard times.",
    "What's a moment when you felt proud of yourself?",
    "Who makes you laugh when you need it most?",
    "What's something you're looking forward to?",
    "Describe a time when you overcame something difficult."
  ];

  useEffect(() => {
    loadHopeKitItems();
  }, [user?.id]);

  const loadHopeKitItems = async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('hope_kit_items')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setHopeKitItems((data || []) as HopeKitItem[]);
    } catch (error: any) {
      console.error('Error loading hope kit items:', error);
      toast.error(error.message || 'Failed to load memory box');
    } finally {
      setLoading(false);
    }
  };

  const addHopeKitItem = async () => {
    if (!user?.id) {
      toast.error('Please log in to save memories');
      return;
    }
    
    if (!newItem.content_text?.trim()) {
      toast.error('Please add some content');
      return;
    }

    try {
      const { data, error } = await supabase
        .from('hope_kit_items')
        .insert({
          user_id: user.id,
          type: newItem.type,
          content_text: newItem.content_text,
          caption: newItem.caption
        })
        .select()
        .single();

      if (error) throw error;
      
      setHopeKitItems([data as HopeKitItem, ...hopeKitItems]);
      setNewItem({ type: 'text', content_text: '', caption: '' });
      setShowAddForm(false);
      toast.success('Added to your memory box ❤️');
    } catch (error: any) {
      console.error('Error adding hope kit item:', error);
      toast.error(error.message || 'Failed to add item');
    }
  };

  const deleteHopeKitItem = async (id: string) => {
    try {
      const { error } = await supabase
        .from('hope_kit_items')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      setHopeKitItems(hopeKitItems.filter(item => item.id !== id));
      toast.success('Item removed from memory box');
    } catch (error) {
      console.error('Error deleting hope kit item:', error);
      toast.error('Failed to remove item');
    }
  };

  const playRandomMemory = () => {
    if (hopeKitItems.length === 0) {
      toast.error('No memories to play yet. Add some first!');
      return;
    }
    
    const randomItem = hopeKitItems[Math.floor(Math.random() * hopeKitItems.length)];
    
    // Scroll to the random item and highlight it briefly
    const element = document.getElementById(`memory-${randomItem.id}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      element.classList.add('bg-primary/10', 'border-primary/30');
      setTimeout(() => {
        element.classList.remove('bg-primary/10', 'border-primary/30');
      }, 3000);
    }
    
    toast.success('Here\'s a memory to remind you of good times');
  };

  const usePrompt = (prompt: string) => {
    setNewItem({ ...newItem, content_text: prompt });
    setShowAddForm(true);
  };

  if (loading) {
    return <div className="flex items-center justify-center p-8">Loading your memory box...</div>;
  }

  if (showHopeReelCreator) {
    return <HopeReelCreator user={user} onStopEditing={() => setShowHopeReelCreator(false)} />;
  }

  return (
    <div className="space-y-6">
      <Card className="border-primary/20 bg-gradient-therapeutic">
        <CardHeader>
          <CardTitle className="text-primary flex items-center gap-2">
            <Heart className="h-5 w-5" />
            Memory Box & Hope Kit
          </CardTitle>
          <p className="text-foreground">
            A collection of positive memories, reasons to stay, and things that bring you comfort.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2 flex-wrap">
            <Button
              onClick={() => setShowAddForm(true)}
              className="bg-primary hover:bg-primary/90"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Memory
            </Button>
            <Button
              onClick={playRandomMemory}
              variant="outline"
              className="border-secondary hover:bg-secondary/10"
            >
              <Shuffle className="h-4 w-4 mr-2" />
              Random Memory
            </Button>
            <Button
              onClick={() => setShowHopeReelCreator(true)}
              variant="outline"
              className="border-primary hover:bg-primary/10 text-primary"
            >
              <Film className="h-4 w-4 mr-2" />
              Create Hope Reel
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Quick Prompts */}
      {!showAddForm && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Quick Prompts</CardTitle>
            <p className="text-muted-foreground text-sm">
              Click a prompt to quickly add a memory
            </p>
          </CardHeader>
          <CardContent>
            <div className="grid gap-2">
              {hopePrompts.map((prompt, index) => (
                <Button
                  key={index}
                  variant="outline"
                  className="text-left justify-start h-auto p-3 whitespace-normal"
                  onClick={() => usePrompt(prompt)}
                >
                  {prompt}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add Form */}
      {showAddForm && (
        <Card className="border-secondary/20 bg-secondary/10">
          <CardHeader>
            <CardTitle className="text-primary">Add New Memory</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Type</Label>
              <div className="flex gap-2 mt-2">
                <Button
                  variant={newItem.type === 'text' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setNewItem({...newItem, type: 'text'})}
                >
                  <Type className="h-4 w-4 mr-2" />
                  Text
                </Button>
                <Button
                  variant={newItem.type === 'image' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setNewItem({...newItem, type: 'image'})}
                >
                  <Image className="h-4 w-4 mr-2" />
                  Image
                </Button>
                <Button
                  variant={newItem.type === 'audio' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setNewItem({...newItem, type: 'audio'})}
                >
                  <Mic className="h-4 w-4 mr-2" />
                  Audio
                </Button>
              </div>
            </div>

            <div>
              <Label htmlFor="content">Content</Label>
              <Textarea
                id="content"
                value={newItem.content_text}
                onChange={(e) => setNewItem({...newItem, content_text: e.target.value})}
                placeholder="Share your memory, reason for living, or something that brings you comfort..."
                rows={4}
              />
            </div>

            <div>
              <Label htmlFor="caption">Caption (optional)</Label>
              <Input
                id="caption"
                value={newItem.caption}
                onChange={(e) => setNewItem({...newItem, caption: e.target.value})}
                placeholder="A title or description for this memory..."
              />
            </div>

            <div className="flex gap-2">
              <Button onClick={addHopeKitItem} className="bg-secondary hover:bg-secondary/90">
                <Plus className="h-4 w-4 mr-2" />
                Add Memory
              </Button>
              <Button onClick={() => setShowAddForm(false)} variant="outline">
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Memory Items */}
      <div className="space-y-4">
        {hopeKitItems.length === 0 ? (
          <Card>
            <CardContent className="text-center py-8">
              <Heart className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">
                Your memory box is empty. Start adding memories, reasons for living, and things that bring you comfort.
              </p>
            </CardContent>
          </Card>
        ) : (
          hopeKitItems.map((item) => (
            <Card key={item.id} id={`memory-${item.id}`} className="transition-colors duration-300">
              <CardContent className="p-4">
                <div className="flex justify-between items-start mb-3">
                  <Badge variant="outline" className="mb-2">
                    {item.type === 'text' && <Type className="h-3 w-3 mr-1" />}
                    {item.type === 'image' && <Image className="h-3 w-3 mr-1" />}
                    {item.type === 'audio' && <Mic className="h-3 w-3 mr-1" />}
                    {item.type}
                  </Badge>
                  <Button
                    onClick={() => deleteHopeKitItem(item.id!)}
                    size="sm"
                    variant="ghost"
                    className="text-destructive hover:text-destructive/80"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>

                {item.caption && (
                  <h4 className="font-semibold mb-2 text-lg">{item.caption}</h4>
                )}

                <div className="whitespace-pre-wrap text-muted-foreground">
                  {item.content_text}
                </div>

                {item.created_at && (
                  <div className="text-xs text-muted-foreground mt-3">
                    Added {new Date(item.created_at).toLocaleDateString()}
                  </div>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};